import axios, { type AxiosResponse } from 'axios';
import { ApiError, type ApiErrorDetail } from '../api-error';

// Every API response is wrapped in this envelope.
export interface Envelope {
  success: boolean;
  data?: unknown;
  meta?: { nextCursor: string | null };
  error?: { code: string; message: string; requestId?: string; details?: ApiErrorDetail[] };
}

function isEnvelope(body: unknown): body is Envelope {
  return typeof body === 'object' && body !== null && 'success' in body;
}

function fromEnvelope(status: number, body: unknown): ApiError {
  const error = isEnvelope(body) ? body.error : undefined;
  if (!error) return new ApiError(status, 'HTTP_ERROR', `Request failed (${status})`);
  return new ApiError(status, error.code, error.message, error.requestId, error.details);
}

// A 2xx is only a success when the envelope says so; a non-JSON body (e.g. a proxy page) is not.
export function checkEnvelope(response: AxiosResponse): AxiosResponse<Envelope> {
  const body: unknown = response.data;
  if (isEnvelope(body) && body.success) return { ...response, data: body };
  throw fromEnvelope(response.status, body);
}

// Turns every failure into an ApiError so callers handle one error type. Cancellations pass
// through untouched: React Query expects them when it aborts a request.
export function toApiError(error: unknown): never {
  if (error instanceof ApiError || axios.isCancel(error) || !axios.isAxiosError(error)) throw error;

  if (error.response) throw fromEnvelope(error.response.status, error.response.data);
  if (error.code === 'ETIMEDOUT') {
    throw new ApiError(0, 'TIMEOUT', 'The request timed out. Please try again.');
  }
  throw new ApiError(0, 'NETWORK_ERROR', "Can't reach the server. Check your connection.");
}
